import React from "react";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import CartProductGrouping from "@src/components/widgets/CartProductGrouping";
import { Link } from "react-router-dom";
import OrderSummaryTile, {
  OrderSummaryTileViews,
} from "@src/components/widgets/OrderSummaryTile";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { useCartStore } from "@src/state/cartState";
import { useSavedForLaterStore } from "@src/state/savedForLaterState";
import SavedForLaterProductTile from "@src/components/widgets/SavedForLaterProductTile";
import { GridContainer } from "@swordsweeper/ssi-ui-component-library";
import Tile, { TileThemes } from "@src/components/containers/Tile";
import styles from "./CartPage.module.scss";
import ProductRecommendations, {
  ProductRecommendationTypes,
} from "@src/components/widgets/ProductRecommendations";

function OutOfStockWarning({
  itemName,
  count,
}: {
  itemName: string;
  count: number;
}): React.JSX.Element {
  const remainder = count - 1;

  return (
    <div>
      We&apos;re sorry, but <b>{itemName}</b>
      {count > 1 ? (
        <>
          {" "}
          and {remainder} other product{remainder === 1 ? "" : "s"} are out of
          stock. We&apos;ve saved them for later so they can be re-added to the
          cart when they&apos;re back in stock. Please contact your distributor
          with any questions.
        </>
      ) : (
        <>
          {" "}
          is out of stock. We&apos;ve saved it for later so it can be re-added
          to the cart when it&apos;s back in stock. Please contact your
          distributor with any questions.
        </>
      )}
    </div>
  );
}

function CartPage(): React.JSX.Element {
  const { showToast } = useToast();
  const { cart, loading: isLoadingCart } = useCartStore();
  const {
    savedForLaters,
    loading: isLoadingSavedForLater,
    fetchSavedForLater,
    refreshSavedForLater,
  } = useSavedForLaterStore();
  const savedForLaterItems = React.useMemo(
    () => savedForLaters ?? [],
    [savedForLaters],
  );

  React.useEffect(() => {
    fetchSavedForLater();
  }, [fetchSavedForLater]);

  const outOfStockProducts = React.useMemo(() => {
    if (!cart?.distributorProductGroupings?.length) {
      return [];
    }

    return cart.distributorProductGroupings.flatMap(
      (grouping) =>
        grouping.products?.stockUnavailable ||
        grouping.products?.stock_unavailable ||
        [],
    );
  }, [cart]);

  const autoMovedOutOfStock = cart?.autoMovedOutOfStock || null;
  const autoMovedOutOfStockCount =
    autoMovedOutOfStock?.removedCount ?? autoMovedOutOfStock?.movedCount ?? 0;
  const autoMovedOutOfStockItemName =
    autoMovedOutOfStock?.firstItemName ?? null;
  const autoMovedOutOfStockToastCount = autoMovedOutOfStock?.movedCount ?? 0;
  const lastAutoMoveToastRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!autoMovedOutOfStockToastCount) return;
    const cartProductIds = autoMovedOutOfStock?.cartProductIds ?? [];
    const signature = `${autoMovedOutOfStockToastCount}:${autoMovedOutOfStockItemName ?? ""}:${cartProductIds.join(
      ",",
    )}`;
    if (lastAutoMoveToastRef.current === signature) return;
    lastAutoMoveToastRef.current = signature;
    showToast({
      message: `Moved ${autoMovedOutOfStockToastCount} out-of-stock item${autoMovedOutOfStockToastCount === 1 ? "" : "s"} to Saved for Later`,
      variant: "success",
    });
  }, [
    autoMovedOutOfStock,
    autoMovedOutOfStockItemName,
    autoMovedOutOfStockToastCount,
    showToast,
  ]);

  React.useEffect(() => {
    const movedCount = autoMovedOutOfStock?.movedCount;
    if (!movedCount) return;
    refreshSavedForLater();
  }, [autoMovedOutOfStock?.movedCount, refreshSavedForLater]);

  const outOfStockCount = outOfStockProducts.length;
  const outOfStockItemName =
    outOfStockProducts[0]?.itemName ??
    outOfStockProducts[0]?.product?.itemName ??
    "This item";
  const warningItemName =
    outOfStockCount > 0
      ? outOfStockItemName
      : (autoMovedOutOfStockItemName ?? "This item");
  const warningCount =
    outOfStockCount > 0 ? outOfStockCount : autoMovedOutOfStockCount;
  const warningRemainder = warningCount - 1;

  return (
    <>
      {(isLoadingCart || isLoadingSavedForLater) && <SpinningLoader />}
      {!!cart && (
        <div className="spaced-content-v-xl">
          <div>
            <SectionHeader
              header={
                <span>
                  <span className="text-xl" data-testid="cart-page-header-cart">
                    Cart
                  </span>

                  <span className="font-weight-normal margin-left-lg">
                    Subtotal:{" "}
                    <PriceDisplay
                      data-testid="cart-page-header-price-display"
                      amount={cart.subtotal}
                    />
                    &nbsp;
                    <span className="text-lightest text-sm">|</span>
                    &nbsp;
                    {cart.itemCount} item{cart.itemCount === 1 ? "" : "s"}
                  </span>
                </span>
              }
            />

            <div className="flex-row spaced-content-lg">
              <div className="flex-3 spaced-content-v-lg">
                {(outOfStockCount > 0 || autoMovedOutOfStockCount > 0) && (
                  <Tile
                    theme={TileThemes.Warning}
                    className={`margin-bottom-lg ${styles.outOfStockTile}`}
                    data-testid="cart-page-out-of-stock-warning-tile"
                  >
                    <OutOfStockWarning
                      itemName={warningItemName}
                      count={warningCount}
                    />
                    {savedForLaterItems.length > 0 && (
                      <div className="margin-top-sm">
                        <a
                          className={styles.savedForLaterLink}
                          href="#saved-for-later"
                        >
                          View Saved for Later
                        </a>
                      </div>
                    )}
                  </Tile>
                )}
                {cart.distributorProductGroupings?.map(
                  (distributorProductGrouping) => (
                    <React.Fragment
                      key={`distributor-product-grouping-${distributorProductGrouping.distributorId}`}
                    >
                      <CartProductGrouping
                        distributorSlug={
                          distributorProductGrouping.distributorSlug
                        }
                        arrivesByDate={distributorProductGrouping.arrivesByDate}
                        itemCount={distributorProductGrouping.itemCount}
                        subtotal={distributorProductGrouping.subtotal}
                        availableProducts={
                          distributorProductGrouping.products.available
                        }
                        backorderedProducts={
                          distributorProductGrouping.products.backordered
                        }
                        priceUnavailableProducts={
                          distributorProductGrouping.products.priceUnavailable
                        }
                        stockUnavailableProducts={
                          distributorProductGrouping.products.stockUnavailable
                        }
                      />
                    </React.Fragment>
                  ),
                )}

                {savedForLaterItems.length > 0 && (
                  <>
                    <div id="saved-for-later">
                      <Tile
                        className={styles.savedForLaterCard}
                        data-testid="cart-page-saved-for-later-tile"
                      >
                        <div className={styles.savedForLaterHeader}>
                          Saved for Later
                        </div>
                        <GridContainer tileSize="520px" gap="var(--margin-lg)">
                          {savedForLaterItems.map((savedItem) => (
                            <SavedForLaterProductTile
                              key={`saved-for-later-${savedItem.id}`}
                              savedForLater={savedItem}
                            />
                          ))}
                        </GridContainer>
                      </Tile>
                    </div>
                  </>
                )}
                <ProductRecommendations
                  type={ProductRecommendationTypes.PastPurchases}
                  count={4}
                  renderSectionHeader={() => (
                    <div className="margin-top-lg">
                      <SectionHeader
                        header={
                          <span className="text-lg text-light">
                            Forgetting Anything?
                          </span>
                        }
                        rightSideContent={
                          <Link
                            className="text-xs"
                            to="/product-recommendations/past-purchases"
                            data-testid="cart-page-past-purchases-recommendations-link"
                          >
                            View My Reordered Items
                          </Link>
                        }
                      />
                    </div>
                  )}
                />
              </div>

              <div className="flex-1 spaced-content-v-lg">
                <OrderSummaryTile
                  cart={cart}
                  view={OrderSummaryTileViews.Cart}
                />
                {/* Note: Commented out for Beta due to missing functionality */}
                {/* <SavingsAndRewardsTile
                  savingsAndRewardsList={cart.savingsAndRewards}
                /> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CartPage;
